import React, { useCallback, useEffect, useState } from 'react';
import { debounce, throttle } from 'lodash';
import { useTranslate } from 'react-admin';

import { Grid } from '@material-ui/core';

import { SelectInput, TextInput } from '~/components';

import { createConsent, CreateOrUpdateConsentRequest, updateConsent } from '~/api/accountTransfer';
import { Account, Amount } from '~/types';
import { HeadTransaction } from '~/types/HeadTransaction';
import { ConsentHolder, RunTransferActionFormProps } from './RunTransferActionTypes';

import { l } from './RunTransferAction';
import AmountInput from '~/components/AmountInput';
import AccountTableSingleSelect from '~/components/AccountTableSingleSelect/AccountTableSingleSelect';

type AccountTransferSession = {
  isSending: boolean;
  nextRequest?: CreateOrUpdateConsentRequest;
  lastConsent?: HeadTransaction;
};

const ACCOUNT_TRANSFER_SESSION: AccountTransferSession = {
  isSending: false,
};

const NO_CONSENT: ConsentHolder = {};

type Direction = 'between' | 'toProfile' | 'fromProfile';

const AccountTransferForm = ({ resource, profileId, onConsent }: RunTransferActionFormProps) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const isTenantResource = resource === 'tenant/accounts';
  const [direction, setDirection] = useState<Direction>();
  const [debitAccountNumber, setDebitAccountNumber] = useState<string | undefined>();
  const [creditAccountNumber, setCreditAccountNumber] = useState<string | undefined>();
  const [debitAccount, setDebitAccount] = useState<Account | undefined>();
  const [creditAccount, setCreditAccount] = useState<Account | undefined>();
  const [comment, setComment] = useState<string | undefined>();
  const [reference, setReference] = useState<string | undefined>();
  const [amount, setAmount] = useState<Amount | null>();

  const senderIsProfile = direction === 'between' || direction === 'fromProfile';
  const recipientIsProfile = direction === 'between' || direction === 'toProfile';

  const debouncedSetComment = useCallback(debounce(setComment, 500), []);
  const debouncedSetReference = useCallback(debounce(setReference, 500), []);
  const debouncedSetDebitAccountNumber = useCallback(debounce(setDebitAccountNumber, 500), []);
  const debouncedSetCreditAccountNumber = useCallback(debounce(setCreditAccountNumber, 500), []);

  useEffect(() => {
    ACCOUNT_TRANSFER_SESSION.isSending = false;
    ACCOUNT_TRANSFER_SESSION.nextRequest = undefined;
    ACCOUNT_TRANSFER_SESSION.lastConsent = undefined;
  }, []);

  const resetAccounts = () => {
    setDebitAccount(undefined);
    setDebitAccountNumber(undefined);
    setCreditAccount(undefined);
    setCreditAccountNumber(undefined);
  };

  const throttledMakeConsent = useCallback(
    throttle(
      (
        debitAccountNumber: string,
        creditAccountNumber: string,
        amount: Amount,
        comment?: string,
        reference?: string
      ): any => {
        const request = {
          senderAccountNumber: debitAccountNumber,
          recipientAccountNumber: creditAccountNumber,
          amount: amount,
          comment: comment,
          referenceId: reference,
        };

        const sendNextRequest = (request: CreateOrUpdateConsentRequest) => {
          if (ACCOUNT_TRANSFER_SESSION.isSending) {
            ACCOUNT_TRANSFER_SESSION.nextRequest = request;
          } else {
            ACCOUNT_TRANSFER_SESSION.isSending = true;
            ACCOUNT_TRANSFER_SESSION.nextRequest = undefined;

            if (ACCOUNT_TRANSFER_SESSION.lastConsent) {
              updateConsent(ACCOUNT_TRANSFER_SESSION.lastConsent.id, request)
                .then((response) => {
                  ACCOUNT_TRANSFER_SESSION.isSending = false;
                  ACCOUNT_TRANSFER_SESSION.lastConsent = response.data;

                  const nextRequest = ACCOUNT_TRANSFER_SESSION.nextRequest;
                  if (nextRequest) {
                    ACCOUNT_TRANSFER_SESSION.nextRequest = undefined;
                    sendNextRequest(nextRequest);
                  }

                  onConsent({ consent: response.data });
                })
                .catch((e) => {
                  const errorCode = e?.body?.code;
                  ACCOUNT_TRANSFER_SESSION.isSending = false;

                  const nextRequest = ACCOUNT_TRANSFER_SESSION.nextRequest;
                  if (nextRequest) {
                    ACCOUNT_TRANSFER_SESSION.nextRequest = undefined;
                    sendNextRequest(nextRequest);
                  }

                  onConsent({ error: errorCode });
                });
            } else {
              createConsent(request)
                .then((response) => {
                  ACCOUNT_TRANSFER_SESSION.isSending = false;
                  ACCOUNT_TRANSFER_SESSION.lastConsent = response.data;

                  const nextRequest = ACCOUNT_TRANSFER_SESSION.nextRequest;
                  if (nextRequest) {
                    ACCOUNT_TRANSFER_SESSION.nextRequest = undefined;
                    sendNextRequest(nextRequest);
                  }

                  onConsent({ consent: response.data });
                })
                .catch((e) => {
                  const errorCode = e?.body.code;
                  ACCOUNT_TRANSFER_SESSION.isSending = false;

                  const nextRequest = ACCOUNT_TRANSFER_SESSION.nextRequest;
                  if (nextRequest) {
                    ACCOUNT_TRANSFER_SESSION.nextRequest = undefined;
                    sendNextRequest(nextRequest);
                  }

                  onConsent({ error: errorCode });
                });
            }
          }
        };

        sendNextRequest(request);
      },
      1000,
      {
        leading: true,
      }
    ),
    []
  );

  useEffect(() => {
    if (debitAccountNumber && creditAccountNumber && amount && comment) {
      onConsent(NO_CONSENT);
      throttledMakeConsent(debitAccountNumber, creditAccountNumber, amount, comment, reference);
    } else {
      onConsent(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onConsent,
    throttledMakeConsent,
    debitAccountNumber,
    creditAccountNumber,
    amount,
    comment,
    reference,
  ]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <SelectInput<Direction | undefined>
          options={[
            {
              value: 'between',
              text: isTenantResource ? t('directionBetweenTenant') : t('directionBetween'),
            },
            {
              value: 'toProfile',
              text: isTenantResource ? t('directionToTenant') : t('directionToProfile'),
            },
            {
              value: 'fromProfile',
              text: isTenantResource ? t('directionFromTenant') : t('directionFromProfile'),
            },
          ]}
          label={t('direction')}
          value={direction}
          style={{ width: 490 }}
          onChange={(e) => {
            setDirection(e.target.value as Direction | undefined);
            resetAccounts();
          }}
          required
          helperText=' '
        />
      </Grid>
      <Grid item>
        {senderIsProfile ? (
          <AccountTableSingleSelect
            resource={resource}
            profileId={profileId}
            label={t('debitAccountNumber')}
            value={debitAccountNumber}
            onClear={() => {
              setDebitAccount(undefined);
              setDebitAccountNumber(undefined);
            }}
            onChange={(account) => {
              setDebitAccount(account);
              setDebitAccountNumber(account.number);
            }}
            beforeOkCheck={(account) => account.id !== creditAccount?.id}
            beforeOkErrorMessage={t('sameAccountError')}
            additionalFilters={{ type: 'REGULAR', status: 'ACTIVE' }}
            disabled={!direction}
            required
            chipVariant='balance'
            style={{ width: 240 }}
            filters={isTenantResource ? ['number', 'currency'] : ['number', 'alias', 'currency']}
          />
        ) : (
          <TextInput
            label={t('debitAccountNumber')}
            disabled={!direction}
            required
            onChange={(e: any) => {
              setDebitAccount(undefined);
              debouncedSetDebitAccountNumber(e.target.value || undefined);
            }}
            style={{ width: 240 }}
          />
        )}
      </Grid>
      <Grid item>
        {recipientIsProfile ? (
          <AccountTableSingleSelect
            resource={resource}
            profileId={profileId}
            label={t('creditAccountNumber')}
            value={creditAccountNumber}
            onClear={() => {
              setCreditAccount(undefined);
              setCreditAccountNumber(undefined);
            }}
            onChange={(account) => {
              setCreditAccount(account);
              setCreditAccountNumber(account.number);
            }}
            beforeOkCheck={(account) => account.id !== debitAccount?.id}
            beforeOkErrorMessage={t('sameAccountError')}
            additionalFilters={{ type: 'REGULAR', status: 'ACTIVE' }}
            disabled={!direction}
            required
            chipVariant='balance'
            style={{ width: 240 }}
            filters={isTenantResource ? ['number', 'currency'] : ['number', 'alias', 'currency']}
          />
        ) : (
          <TextInput
            label={t('creditAccountNumber')}
            disabled={!direction}
            required
            onChange={(e: any) => {
              setCreditAccount(undefined);
              debouncedSetCreditAccountNumber(e.target.value || undefined);
            }}
            style={{ width: 240 }}
          />
        )}
      </Grid>
      <Grid item>
        <AmountInput
          onChange={(newAmount) => {
            if (newAmount?.value !== amount?.value || newAmount?.currency !== amount?.currency) {
              setAmount(newAmount);
            }
          }}
          label={t('amount')}
          disabled={!debitAccountNumber || !creditAccountNumber}
          currencyDisabled={!debitAccountNumber || !creditAccountNumber}
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('comment')}
          style={{ width: 490 }}
          multiline
          required
          onChange={(e: any) => {
            debouncedSetComment(e.target.value || undefined);
          }}
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('reference')}
          style={{ width: 282 }}
          onChange={(e: any) => {
            debouncedSetReference(e.target.value || undefined);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default AccountTransferForm;
